import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { FiEye, FiCalendar, FiDollarSign, FiSearch, FiRefreshCw, FiX, FiBriefcase, FiMapPin, FiAward, FiChevronDown, FiChevronUp, FiSliders } from 'react-icons/fi';
import api, { getPublicJobs, previewJobMatch } from '../../../../api/client';
import axios from 'axios';
import B2CInterviewSetupModal from '../../../modals/B2CInterviewSetupModal';
import PremiumSelect from '../../component/PremiumSelect';
import { toast } from 'react-toastify';
import { CircularProgress } from '@mui/material';
import { theme } from '../../../../theme.js';
import '../../admin.css';
import PageHeader, { PageCard, PageHeaderButton, PageShell } from '../../../common/PageHeader';
import { usePageLoading } from '../../../../contexts/LoadingContext';

const getNormalizedCredits = (data) => {
  const payload = data?.results || data || {};
  const primaryCredits = [
    payload.available_credits,
    payload.availableCredits,
    payload.remaining_credits,
    payload.remainingCredits,
    payload.balance,
  ]
    .map((value) => Number(value))
    .find((value) => Number.isFinite(value));

  if (Number.isFinite(primaryCredits)) {
    return primaryCredits;
  }

  const currentBalance = Number(payload.current_balance ?? payload.currentBalance);
  const dailyCredits = Number(payload.daily_credits ?? payload.dailyCredits);

  return (Number.isFinite(currentBalance) ? currentBalance : 0) +
    (Number.isFinite(dailyCredits) ? dailyCredits : 0);
};

const stripHtml = (html) => {
  if (!html) return "";
  try {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    return doc.body.textContent || "";
  } catch {
    return html.replace(/<[^>]*>/g, '');
  }
};

function PublicJobs({ onLogout }) {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedJob, setSelectedJob] = useState(null);
  const [jobDescriptionDialogOpen, setJobDescriptionDialogOpen] = useState(false);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [matchData, setMatchData] = useState(null);

  // External Jobs States
  const [importUrl, setImportUrl] = useState('');
  const [importing, setImporting] = useState(false);
  const [importError, setImportError] = useState('');
  const [importSuccess, setImportSuccess] = useState('');
  const [applyingJobId, setApplyingJobId] = useState(null);

  // Practice Flow States
  const [showPracticeModal, setShowPracticeModal] = useState(false);
  const [selectedPracticeJob, setSelectedPracticeJob] = useState(null);
  const [userResumes, setUserResumes] = useState([]);
  const [selectedResumeData, setSelectedResumeData] = useState(null);
  const [credits, setCredits] = useState(null);

  const { t } = useTranslation();
  usePageLoading(loading, {
    message: t('publicJobDescriptions.loadingOpportunities', 'Loading opportunities…'),
  });

  // Advanced Filter States
  const [selectedDepartment, setSelectedDepartment] = useState('');
  const [locationSearch, setLocationSearch] = useState('');
  const [selectedSalary, setSelectedSalary] = useState('');
  const [selectedExperience, setSelectedExperience] = useState('');
  const [selectedRemote, setSelectedRemote] = useState('');
  const [showFilters, setShowFilters] = useState(true);
  const [showSearchBox, setShowSearchBox] = useState(true);
  const [activeTab, setActiveTab] = useState('internal'); // 'internal' or 'external'

  // Pagination State
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 15;

  const defaultDepartments = [
    "Software Engineering",
    "Data Science",
    "Product Management",
    "Marketing",
    "Sales",
    "Human Resources",
    "Finance",
    "Operations",
    "IT"
  ];

  const availableDepartments = Array.from(
    new Set([
      ...defaultDepartments,
      ...jobs.map((job) => job.department_name).filter(Boolean)
    ])
  ).sort();

  const experienceOptions = [
    { value: '', label: t('publicJobDescriptions.allExperiences', 'All Experiences') },
    { value: 'entry', label: t('publicJobDescriptions.entryLevel', 'Entry Level (0-1 yrs)') },
    { value: 'junior', label: t('publicJobDescriptions.juniorLevel', 'Junior (1-3 yrs)') },
    { value: 'mid', label: t('publicJobDescriptions.midLevel', 'Mid Level (3-5 yrs)') },
    { value: 'senior', label: t('publicJobDescriptions.seniorLevel', 'Senior (5-8 yrs)') },
    { value: 'lead', label: t('publicJobDescriptions.leadLevel', 'Lead/Manager (8-15 yrs)') },
    { value: 'fifteen_plus', label: t('publicJobDescriptions.fifteenPlus', 'Director (15-20 yrs)') },
    { value: 'twenty_plus', label: t('publicJobDescriptions.twentyPlus', 'VP/CXO (20+ yrs)') },
    { value: 'executive', label: t('publicJobDescriptions.executive', 'Executive') }
  ];

  const salaryOptions = [
    { value: '', label: t('publicJobDescriptions.allSalaries', 'All Salaries') },
    { value: 'under_5l', label: t('publicJobDescriptions.under5l', 'Under ₹5L') },
    { value: '5l_10l', label: t('publicJobDescriptions.fiveToTen', '₹5L - ₹10L') },
    { value: '10l_15l', label: t('publicJobDescriptions.tenToFifteen', '₹10L - ₹15L') },
    { value: '15l_20l', label: t('publicJobDescriptions.fifteenToTwenty', '₹15L - ₹20L') },
    { value: '20l_25l', label: t('publicJobDescriptions.twentyToTwentyFive', '₹20L - ₹25L') },
    { value: '25l_30l', label: t('publicJobDescriptions.twentyFiveToThirty', '₹25L - ₹30L') },
    { value: '30l_50l', label: t('publicJobDescriptions.thirtyToFifty', '₹30L - ₹50L') },
    { value: '50l_70l', label: t('publicJobDescriptions.fiftyToSeventy', '₹50L - ₹70L') },
    { value: '70l_1cr', label: t('publicJobDescriptions.seventyToOneCr', '₹70L - ₹1Cr') },
    { value: '1cr_plus', label: t('publicJobDescriptions.oneCrPlus', '₹1Cr+') }
  ];

  const hasActiveFilters =
    selectedDepartment !== '' ||
    locationSearch !== '' ||
    selectedSalary !== '' ||
    selectedExperience !== '' ||
    selectedRemote !== '' ||
    searchTerm !== '';

  const handleClearFilters = () => {
    setSelectedDepartment('');
    setLocationSearch('');
    setSelectedSalary('');
    setSelectedExperience('');
    setSelectedRemote('');
    setSearchTerm('');
    setCurrentPage(1);
  };

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedDepartment, locationSearch, selectedExperience, selectedSalary, selectedRemote]);


  useEffect(() => {
    fetchPublicJobs();
  }, []);

  const fetchPublicJobs = async () => {
    try {
      setLoading(true);
      setError('');

      // Fetch internal jobs
      const internalJobs = await getPublicJobs();
      const internalFormatted = (internalJobs.public_jobs || internalJobs || []).map(job => ({
        ...job,
        location: job.location || 'Remote',
        isExternal: false,
        url: job.public_link
      }));

      // Fetch external jobs from job-finder (fetch all pages)
      let allExternalJobs = [];
      try {
        const jobFinderUrl = process.env.REACT_APP_JOB_FINDER_URL || 'http://localhost:8001';
        const firstPageResp = await axios.get(`${jobFinderUrl}/api/jobs`, { params: { page: 1, page_size: 100 } });
        const totalPages = firstPageResp.data?.total_pages || 1;
        let items = firstPageResp.data?.items || [];
        allExternalJobs = [...items];

        if (totalPages > 1) {
          const promises = [];
          for (let p = 2; p <= totalPages; p++) {
            const jobFinderUrl = process.env.REACT_APP_JOB_FINDER_URL || 'http://localhost:8001';
            promises.push(axios.get(`${jobFinderUrl}/api/jobs`, { params: { page: p, page_size: 100 } }));
          }
          const responses = await Promise.all(promises);
          responses.forEach(resp => {
            if (resp.data?.items) {
              allExternalJobs = [...allExternalJobs, ...resp.data.items];
            }
          });
        }

        allExternalJobs = allExternalJobs.map(job => ({
          ...job,
          id: `ext_${job.id}`,
          title: job.job_title,
          company_name: job.company,
          location: job.location || 'Remote',
          description: `Location: ${job.location || 'Remote'}\nFirst seen: ${job.first_seen}`,
          department_name: 'External Platform',
          required_credits: 0,
          created_at: job.date_found,
          user_application_status: job.applied === 'yes' ? 'Applied' : null,
          isExternal: true,
          url: job.job_url
        }));
      } catch (err) {
        console.error('Failed to fetch external jobs', err);
        // We gracefully continue if the sidecar is down
      }

      // Combine and sort by date descending
      const combinedJobs = [...internalFormatted, ...allExternalJobs].sort((a, b) => {
        const dateA = new Date(a.created_at || a.date_found);
        const dateB = new Date(b.created_at || b.date_found);
        return dateB - dateA;
      });
      setJobs(combinedJobs);
    } catch (err) {
      console.error('Error fetching public jobs:', err);
      setError(t('publicJobDescriptions.failedToLoad'));
    } finally {
      setLoading(false);
    }
  };

  const handleImportJob = async () => {
    if (!importUrl) {
      setImportError('Please enter a valid job URL');
      return;
    }
    try {
      setImporting(true);
      setImportError('');
      setImportSuccess('');
      const jobFinderUrl = process.env.REACT_APP_JOB_FINDER_URL || 'http://localhost:8001';
      await axios.post(`${jobFinderUrl}/api/jobs/import`, { url: importUrl });
      setImportSuccess('Job successfully imported!');
      setImportUrl('');
      fetchPublicJobs();
    } catch (err) {
      console.error('Error importing job:', err);
      setImportError(err.response?.data?.detail || 'Failed to import job.');
    } finally {
      setImporting(false);
    }
  };



  const handlePracticeInterview = async (job) => {
    const token = localStorage.getItem('token');
    if (!token) {
      toast.info(t('publicJobPage.pleaseLoginToApply', 'Please login to start practice interviews.'));
      window.location.href = `/login?redirectTo=${encodeURIComponent(window.location.pathname)}`;
      return;
    }

    setApplyingJobId(job.id);
    try {
      let resumes = userResumes;
      if (resumes.length === 0) {
        const response = await api.get('/resumes/', {
          headers: { Authorization: `Token ${token}` }
        });
        resumes = response.data.results || response.data || [];
        setUserResumes(resumes);
      }

      const activeResume = resumes.find((r) => r.is_active) || resumes[0];
      if (!activeResume) {
        toast.warning(t('publicJobPage.needResumeBeforeApply', 'Please upload a resume in your profile before practicing mock interviews.'));
        window.location.href = '/my-applications';
        return;
      }

      try {
        const creditResponse = await api.get('observability/user-credits/');
        const fetchedCredits = getNormalizedCredits(creditResponse.data);
        setCredits(fetchedCredits);
      } catch (err) {
        console.error('Error fetching credits:', err);
      }

      setSelectedResumeData(activeResume);
      let jobToPractice = job;
      if (job.isExternal && job.job_url) {
        try {
          const jobFinderUrl = process.env.REACT_APP_JOB_FINDER_URL || 'http://localhost:8001';
          const detailsRes = await axios.get(`${jobFinderUrl}/api/jobs/details`, {
            params: { url: job.job_url }
          });
          if (detailsRes.data) {
            jobToPractice = {
              ...job,
              description: detailsRes.data.description || job.description,
              required_skills: detailsRes.data.required_skills || job.required_skills || []
            };
          }
        } catch (extErr) {
          console.error('Failed to extract external JD details:', extErr);
        }
      }
      setSelectedPracticeJob(jobToPractice);
      setShowPracticeModal(true);
    } catch (err) {
      console.error('Error preparing practice interview:', err);
      toast.error('Failed to prepare practice interview. Please try again.');
    } finally {
      setApplyingJobId(null);
    }
  };

  const handleViewJobDescription = (job) => {
    if (job.public_link) {
      window.open(job.public_link, '_blank');
    } else {
      setSelectedJob(job);
      setJobDescriptionDialogOpen(true);
    }
  };

  const handlePreviewMatch = async (job) => {
    try {
      setPreviewLoading(true);
      setSelectedJob(job);
      const matchData = await previewJobMatch(job.id);
      setMatchData(matchData);
      setJobDescriptionDialogOpen(false);
      setPreviewDialogOpen(true);
    } catch (err) {
      console.error('Error previewing job match:', err);
      setError(t('publicJobDescriptions.failedToPreview'));
    } finally {
      setPreviewLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const filteredJobs = jobs.filter(job => {
    if (job.user_application_status) {
      return false;
    }

    // Platform Filter (Internal vs External)
    if (activeTab === 'internal' && job.isExternal) return false;
    if (activeTab === 'external' && !job.isExternal) return false;

    // Text search filter
    if (searchTerm) {
      const lowerSearch = searchTerm.toLowerCase();
      const matchesSearch =
        (job.title && job.title.toLowerCase().includes(lowerSearch)) ||
        (job.company_name && job.company_name.toLowerCase().includes(lowerSearch)) ||
        (job.department_name && job.department_name.toLowerCase().includes(lowerSearch)) ||
        (job.location && job.location.toLowerCase().includes(lowerSearch));
      if (!matchesSearch) return false;
    }

    // Department / Role Type filter
    if (selectedDepartment) {
      if (job.department_name !== selectedDepartment) return false;
    }

    // Location filter
    if (locationSearch) {
      const lowerLoc = locationSearch.toLowerCase();
      const jobLoc = (job.location || '').toLowerCase();
      const matchesLoc = jobLoc.includes(lowerLoc) ||
        (job.title && job.title.toLowerCase().includes(lowerLoc)) ||
        (job.description && job.description.toLowerCase().includes(lowerLoc));
      if (!matchesLoc) return false;
    }

    // Experience Level filter
    if (selectedExperience) {
      const jobExp = job.experience_level;
      if (jobExp) {
        if (jobExp !== selectedExperience) return false;
      } else {
        const text = `${job.title} ${job.description || ''}`.toLowerCase();
        let matchesExp = false;
        if (selectedExperience === 'entry') {
          matchesExp = text.includes('entry') || text.includes('intern') || text.includes('fresh') || text.includes('0-1 year') || text.includes('0 to 1') || text.includes('0+') || text.includes('no experience');
        } else if (selectedExperience === 'junior') {
          matchesExp = text.includes('junior') || text.includes('1-3 year') || text.includes('1 to 3') || text.includes('1+') || text.includes('2+') || text.includes('3+');
        } else if (selectedExperience === 'mid') {
          matchesExp = text.includes('mid') || text.includes('3-5 year') || text.includes('3 to 5') || text.includes('3+') || text.includes('4+') || text.includes('5+');
        } else if (selectedExperience === 'senior') {
          matchesExp = text.includes('senior') || text.includes('5-8 year') || text.includes('5 to 8') || text.includes('5+') || text.includes('6+') || text.includes('7+') || text.includes('8+');
        } else if (selectedExperience === 'lead') {
          matchesExp = text.includes('lead') || text.includes('architect') || text.includes('8+') || text.includes('9+') || text.includes('10+') || text.includes('8-10 year') || text.includes('8 to 10');
        } else if (selectedExperience === 'fifteen_plus') {
          matchesExp = text.includes('15+') || text.includes('15 year') || text.includes('15+ year') || text.includes('16+') || text.includes('17+') || text.includes('18+') || text.includes('19+') || text.includes('20+');
        } else if (selectedExperience === 'twenty_plus') {
          matchesExp = text.includes('20+') || text.includes('20 year') || text.includes('20+ year') || text.includes('25+');
        } else if (selectedExperience === 'executive') {
          matchesExp = text.includes('executive') || text.includes('director') || text.includes('vp') || text.includes('chief') || text.includes('10+ year') || text.includes('10 to');
        }
        if (!matchesExp) return false;
      }
    }

    // Salary Range filter (scale-detection & description parsing fallback)
    if (selectedSalary) {
      const min = job.salary_range_min;
      const max = job.salary_range_max;

      if (min !== undefined && min !== null) {
        const limitMin = min > 150000 ? 500000 : 50000;
        if (selectedSalary === 'under_5l') {
          if (min >= limitMin) return false;
        } else if (selectedSalary === '5l_10l') {
          if (min < limitMin || min > (limitMin * 2)) return false;
        } else if (selectedSalary === '10l_15l') {
          if (min < (limitMin * 2) || min > (limitMin * 3)) return false;
        } else if (selectedSalary === '15l_20l') {
          if (min < (limitMin * 3) || min > (limitMin * 4)) return false;
        } else if (selectedSalary === '20l_25l') {
          if (min < (limitMin * 4) || min > (limitMin * 5)) return false;
        } else if (selectedSalary === '25l_30l') {
          if (min < (limitMin * 5) || min > (limitMin * 6)) return false;
        } else if (selectedSalary === '30l_50l') {
          if (min < (limitMin * 6) || min > (limitMin * 10)) return false;
        } else if (selectedSalary === '50l_70l') {
          if (min < (limitMin * 10) || min > (limitMin * 14)) return false;
        } else if (selectedSalary === '70l_1cr') {
          if (min < (limitMin * 14) || min > (limitMin * 20)) return false;
        } else if (selectedSalary === '1cr_plus') {
          if (min < (limitMin * 20) && (!max || max < (limitMin * 20))) return false;
        }
      } else {
        const text = (job.description || '').toLowerCase();
        const match = text.match(/(\d+(?:\.\d+)?)\s*(?:-|to)?\s*(\d+(?:\.\d+)?)?\s*(?:lpa|lakh|l|lac|cr|crore)/);
        if (match) {
          const isCrore = /cr|crore/.test(match[0]);
          const val1 = parseFloat(match[1]) * (isCrore ? 100 : 1);
          const val2 = match[2] ? parseFloat(match[2]) * (isCrore ? 100 : 1) : val1;
          const val = (val1 + val2) / 2;

          if (selectedSalary === 'under_5l') {
            if (val >= 5) return false;
          } else if (selectedSalary === '5l_10l') {
            if (val < 5 || val > 10) return false;
          } else if (selectedSalary === '10l_15l') {
            if (val < 10 || val > 15) return false;
          } else if (selectedSalary === '15l_20l') {
            if (val < 15 || val > 20) return false;
          } else if (selectedSalary === '20l_25l') {
            if (val < 20 || val > 25) return false;
          } else if (selectedSalary === '25l_30l') {
            if (val < 25 || val > 30) return false;
          } else if (selectedSalary === '30l_50l') {
            if (val < 30 || val > 50) return false;
          } else if (selectedSalary === '50l_70l') {
            if (val < 50 || val > 70) return false;
          } else if (selectedSalary === '70l_1cr') {
            if (val < 70 || val > 100) return false;
          } else if (selectedSalary === '1cr_plus') {
            if (val < 100) return false;
          }
        }
      }
    }

    // Remote Preference / Work Type filter
    if (selectedRemote) {
      const jobWorkType = job.work_type;
      if (jobWorkType) {
        const workType = jobWorkType.toLowerCase().replace('-', '_');
        if (workType !== selectedRemote) return false;
      } else {
        const text = `${job.title} ${job.department_name} ${job.description || ''}`.toLowerCase();
        let matchesRemote = false;
        if (selectedRemote === 'remote') {
          matchesRemote = text.includes('remote');
        } else if (selectedRemote === 'hybrid') {
          matchesRemote = text.includes('hybrid');
        } else if (selectedRemote === 'on_site') {
          matchesRemote = text.includes('on-site') || text.includes('onsite') || text.includes('in-office') || text.includes('on site') || text.includes('in office') || (!text.includes('remote') && !text.includes('hybrid'));
        }
        if (!matchesRemote) return false;
      }
    }

    return true;
  });

  const currentJobs = filteredJobs.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage
  );
  const totalPages = Math.ceil(filteredJobs.length / rowsPerPage);

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const getApplicationStatusColor = (status) => {
    switch (status) {
      case 'applied':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'accepted':
        return 'bg-emerald-100 text-emerald-800 border-emerald-200';
      case 'rejected':
        return 'bg-red-100 text-red-800 border-red-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const JobCard = ({ job }) => (
    <div className="group relative bg-white backdrop-blur-sm border border-slate-200/60 rounded-2xl p-6 h-full flex flex-col transition-all duration-500 hover:-translate-y-2 hover:shadow-2xl hover:shadow-slate-200/50 hover:border-indigo-200/80">
      <div className="absolute inset-0 bg-gradient-to-br from-indigo-50/50 via-transparent to-cyan-50/30 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

      <div className="relative z-10 flex-grow">
        <div className="mb-4">
          <h3 className="text-lg font-semibold text-slate-800 mb-2 group-hover:text-indigo-700 transition-colors duration-300 leading-tight" style={{ fontFamily: theme.fonts.body }}>
            {job.title}
          </h3>
          <p className="text-sm font-medium text-indigo-600 mb-1 group-hover:text-indigo-700 transition-colors duration-300">
            {job.company_name}
          </p>
          <p className="text-sm font-normal text-slate-500">
            {job.department_name}
          </p>
        </div>

        <div className="mb-4">
          <div
            className="text-sm font-normal text-slate-600 leading-relaxed line-clamp-3"
            dangerouslySetInnerHTML={{ __html: job.description }}
          />
        </div>

        <div className="flex flex-wrap gap-2 mb-4">
          <span className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium bg-blue-50/80 text-blue-700 border border-blue-200/60 shadow-sm">
            <FiMapPin className="w-3.5 h-3.5 mr-1.5 text-blue-500" />
            {job.location || 'Remote'}
          </span>
          <span className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium bg-gradient-to-r from-emerald-50 to-teal-50 text-emerald-700 border border-emerald-200/60">
            {job.required_credits} {t('publicJobDescriptions.credits')}
          </span>
          <span className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium bg-slate-50 text-slate-600 border border-slate-200">
            <FiCalendar className="w-3.5 h-3.5 mr-1.5" />
            {formatDate(job.created_at)}
          </span>
        </div>

        {job.user_application_status && (
          <div className="mb-4">
            <span className={`inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium border ${getApplicationStatusColor(job.user_application_status)}`}>
              {t('publicJobDescriptions.status')}: {job.user_application_status}
            </span>
          </div>
        )}
      </div>

      <div className="relative z-10">
        <button
          onClick={() => handleViewJobDescription(job)}
          className="w-full flex items-center justify-center px-6 py-3.5 bg-gradient-primary text-white rounded-xl transition-all duration-300 transform hover:scale-[1.02] hover:shadow-lg shadow-indigo-200/50"
        >
          <FiEye className="w-4 h-4 mr-2" />
          {t('publicJobDescriptions.viewJobDetails')}
        </button>
      </div>
    </div>
  );

  // Enhanced Modal Component
  const Modal = ({ isOpen, onClose, children, maxWidth = "max-w-3xl" }) => {
    if (!isOpen) return null;

    return (
      <div className="fixed inset-0 z-50 overflow-y-auto">
        <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
          <div
            className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm transition-opacity"
            onClick={onClose}
          />

          <span className="hidden sm:inline-block sm:align-middle sm:h-screen">&#8203;</span>

          <div className={`inline-block align-bottom bg-white/95 backdrop-blur-xl rounded-3xl text-left overflow-hidden shadow-2xl transform transition-all sm:my-8 sm:align-middle ${maxWidth} sm:w-full border border-white/20`}>
            {children}
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <PageShell fill={false} className="bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/50" style={{ fontFamily: theme.fonts.body }}>
        <PageCard overflow={false}>
            <PageHeader
              title={t('publicJobDescriptions.title')}
              subtitle={t('publicJobDescriptions.subtitle')}
            >
              <PageHeaderButton
                onClick={() => setShowSearchBox(!showSearchBox)}
                title="Toggle Search & Filters Box"
                icon={<FiSearch className="h-3.5 w-3.5" />}
              >
                {showSearchBox ? 'Hide Search Box' : 'Show Search Box'}
                {showSearchBox ? <FiChevronUp className="h-3.5 w-3.5" /> : <FiChevronDown className="h-3.5 w-3.5" />}
              </PageHeaderButton>
            </PageHeader>
          <div className="p-3 sm:p-4">



          {/* Search and Filter Section */}
          {showSearchBox && (
            <div className="relative z-30 bg-white/80 backdrop-blur-sm rounded-3xl shadow-xl shadow-slate-200/50 p-4 mb-6 border border-white/60 transition-all duration-300">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
                <div className="md:col-span-2">
                  <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-1.5" style={{ fontFamily: theme.fonts.body }}>
                    {t('publicJobDescriptions.searchJobs')}
                  </label>
                  <div className="relative">
                    <FiSearch className="absolute left-4 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4 group-focus-within:text-indigo-500 transition-colors" />
                    <input
                      type="text"
                      placeholder={t('publicJobDescriptions.searchPlaceholder')}
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-11 pr-4 py-2.5 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-300 bg-white/70 backdrop-blur-sm text-slate-700 text-sm font-medium"
                    />
                  </div>
                </div>

                <div className="flex md:col-span-2 gap-3 items-center justify-end">
                  <button
                    type="button"
                    onClick={() => setShowFilters(!showFilters)}
                    className={`px-3.5 py-2.5 rounded-xl font-medium text-sm flex items-center justify-center gap-2 border transition-all duration-200 shadow-sm ${
                      showFilters
                        ? 'bg-indigo-50 border-indigo-200 text-indigo-700 hover:bg-indigo-100'
                        : 'bg-white border-slate-200 text-slate-700 hover:bg-slate-50'
                    }`}
                    title="Toggle Advanced Filters"
                  >
                    <FiSliders className="w-4 h-4 text-indigo-500" />
                    <span>Filters</span>
                    {showFilters ? <FiChevronUp className="w-4 h-4" /> : <FiChevronDown className="w-4 h-4" />}
                  </button>
                  <button
                    type="button"
                    onClick={fetchPublicJobs}
                    disabled={loading}
                    className="flex-1 sm:flex-initial px-4 py-2.5 bg-gradient-to-r from-slate-600 to-slate-700 hover:from-slate-700 hover:to-slate-800 text-white rounded-xl transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed font-medium text-sm flex items-center justify-center shadow-sm"
                  >
                    <FiRefreshCw className={`w-4 h-4 mr-1.5 ${loading ? 'animate-spin' : ''}`} />
                    {t('publicJobDescriptions.refresh')}
                  </button>
                  {hasActiveFilters && (
                    <button
                      type="button"
                      onClick={handleClearFilters}
                      className="px-3.5 py-2.5 text-red-600 hover:text-red-700 transition-colors border border-red-200 hover:bg-red-50 rounded-xl font-medium text-sm flex items-center justify-center gap-1.5 shadow-sm"
                    >
                      <FiX className="w-4 h-4" />
                      {t('publicJobDescriptions.clearFilters')}
                    </button>
                  )}
                </div>
              </div>

              {/* Filter Fields Row */}
              {showFilters && (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3.5 border-t border-slate-100 pt-3 mt-3 transition-all duration-300">
                  {/* Role Type / Department */}
                  <div className="flex flex-col">
                    <label className="text-sm font-semibold text-slate-500 mb-1.5">{t('publicJobDescriptions.roleType', 'Role Type')}</label>
                    <PremiumSelect
                      variant="b2c"
                      icon={FiBriefcase}
                      iconColor="text-indigo-500"
                      value={selectedDepartment}
                      onChange={(val) => setSelectedDepartment(val)}
                      placeholder={t('publicJobDescriptions.departments', 'Departments')}
                      options={[
                        { value: '', label: t('publicJobDescriptions.allDepartments', 'All Departments') },
                        ...availableDepartments.map((dept) => ({ value: dept, label: dept }))
                      ]}
                    />
                  </div>

                  {/* Location */}
                  <div className="flex flex-col">
                    <label className="text-sm font-semibold text-slate-500 mb-1.5">{t('publicJobDescriptions.location', 'Location')}</label>
                    <div className="relative">
                      <FiMapPin className="absolute left-3.5 top-1/2 transform -translate-y-1/2 text-indigo-500 w-4 h-4 pointer-events-none" />
                      <input
                        type="text"
                        placeholder={t('publicJobDescriptions.locationPlaceholder', 'e.g. Bengaluru')}
                        value={locationSearch}
                        onChange={(e) => setLocationSearch(e.target.value)}
                        className="w-full pl-9 pr-3 py-2.5 bg-white border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-200 text-slate-700 text-sm font-medium shadow-2xs"
                      />
                    </div>
                  </div>

                  {/* Experience Level */}
                  <div className="flex flex-col">
                    <label className="text-sm font-semibold text-slate-500 mb-1.5">{t('publicJobDescriptions.experienceLevel', 'Experience Level')}</label>
                    <PremiumSelect
                      variant="b2c"
                      icon={FiAward}
                      iconColor="text-indigo-500"
                      value={selectedExperience}
                      onChange={(val) => setSelectedExperience(val)}
                      placeholder={t('publicJobDescriptions.experienceLevel', 'Experience Level')}
                      searchable={false}
                      options={experienceOptions.map((opt) => ({
                        value: opt.value,
                        label: opt.label
                      }))}
                    />
                  </div>

                  {/* Salary Range */}
                  <div className="flex flex-col">
                    <label className="text-sm font-semibold text-slate-500 mb-1.5">{t('publicJobDescriptions.salaryRange', 'Salary Range')}</label>
                    <PremiumSelect
                      variant="b2c"
                      icon={FiDollarSign}
                      iconColor="text-indigo-500"
                      value={selectedSalary}
                      onChange={(val) => setSelectedSalary(val)}
                      placeholder={t('publicJobDescriptions.salaryRange', 'Salary Range')}
                      searchable={false}
                      options={salaryOptions.map((opt) => ({
                        value: opt.value,
                        label: opt.label
                      }))}
                    />
                  </div>

                  {/* Remote Preference */}
                  <div className="flex flex-col">
                    <label className="text-sm font-semibold text-slate-500 mb-1.5">{t('publicJobDescriptions.remotePreference', 'Remote Preference')}</label>
                    <div className="flex bg-slate-100 p-0.5 rounded-xl border border-slate-200 h-[38px] items-center">
                      {[
                        { value: 'remote', label: t('publicJobDescriptions.remote', 'Remote') },
                        { value: 'on_site', label: t('publicJobDescriptions.onSite', 'On-site') },
                        { value: 'hybrid', label: t('publicJobDescriptions.hybrid', 'Hybrid') },
                      ].map((option) => {
                        const isSelected = selectedRemote === option.value;
                        return (
                          <button
                            key={option.value}
                            type="button"
                            onClick={() => setSelectedRemote(selectedRemote === option.value ? '' : option.value)}
                            className={`flex-1 h-full py-1 px-1.5 rounded-lg text-[13px] font-bold transition-all duration-200 truncate ${isSelected
                              ? 'bg-indigo-600 text-white shadow-2xs'
                              : 'text-slate-600 hover:text-slate-800 hover:bg-slate-200/50'
                              }`}
                          >
                            {option.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Error Alert */}
          {error && (
            <div className="bg-red-50 border-l-4 border-red-400 rounded-2xl p-6 mb-8 shadow-lg">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-red-800">{error}</p>
                </div>
              </div>
            </div>
          )}

          {/* Jobs Section Header */}
          <div className="bg-white/80 backdrop-blur-sm rounded-3xl shadow-xl shadow-slate-200/50 p-8 border border-white/60">
            <div className="mb-8">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-3">
                <div className="flex items-center gap-4">
                  <h2 className="text-2xl font-bold text-slate-800" style={{ fontFamily: theme.fonts.body, marginBottom: 0 }}>
                    {t('publicJobDescriptions.availableOpportunities')}
                  </h2>
                  {!loading && (
                    <span className="inline-flex items-center px-4 py-2 rounded-full text-sm font-semibold bg-gradient-primary text-white shadow-lg">
                      {filteredJobs.length} {filteredJobs.length === 1 ? t('publicJobDescriptions.job') : t('publicJobDescriptions.jobs')}
                    </span>
                  )}
                </div>

                <div className="flex bg-slate-100 p-1 rounded-xl border border-slate-200 w-full sm:w-auto self-start sm:self-auto">
                  <button
                    onClick={() => { setActiveTab('internal'); setCurrentPage(1); }}
                    className={`flex-1 sm:flex-none px-6 py-2 rounded-lg text-sm font-semibold transition-all ${
                      activeTab === 'internal'
                        ? 'bg-white text-indigo-600 shadow-sm border border-slate-200/50'
                        : 'text-slate-500 hover:text-slate-800'
                    }`}
                  >
                    Aptiview Jobs
                  </button>
                  <button
                    onClick={() => { setActiveTab('external'); setCurrentPage(1); }}
                    className={`flex-1 sm:flex-none px-6 py-2 rounded-lg text-sm font-semibold transition-all ${
                      activeTab === 'external'
                        ? 'bg-white text-indigo-600 shadow-sm border border-slate-200/50'
                        : 'text-slate-500 hover:text-slate-800'
                    }`}
                  >
                    External Platforms
                  </button>
                </div>
              </div>
              {!loading && filteredJobs.length > 0 && searchTerm && (
                <p className="text-sm font-normal text-slate-500 bg-slate-100 px-3 py-1 rounded-full inline-block">
                  {t('publicJobDescriptions.filteredBy')}: "{searchTerm}"
                </p>
              )}
            </div>

            {/* Enhanced Data Table */}
            {loading ? null : filteredJobs.length === 0 ? (
              <div className="text-center py-20">
                <div className="text-6xl mb-4 opacity-60">🔍</div>
                <h3 className="text-xl font-semibold text-slate-800 mb-2">
                  {searchTerm ? t('publicJobDescriptions.noJobsMatchSearch') : t('publicJobDescriptions.noPublicJobs')}
                </h3>
                <p className="text-sm font-normal text-slate-500 max-w-md mx-auto">
                  {searchTerm ? t('publicJobDescriptions.adjustSearch') : t('publicJobDescriptions.checkBackLater')}
                </p>
              </div>
            ) : (
              <div className="overflow-hidden rounded-2xl border border-slate-200/60 bg-white">
                <div className="overflow-x-auto">
                  <table className="w-full text-left border-collapse">
                    <thead>
                      <tr className="bg-slate-50 border-b border-slate-200 text-slate-500 text-sm font-semibold">
                        <th className="px-6 py-4">Job Role</th>
                        <th className="px-6 py-4">Department / Source</th>
                        <th className="px-6 py-4 hidden md:table-cell">Location</th>
                        <th className="px-6 py-4 hidden md:table-cell">Date Posted</th>
                        <th className="px-6 py-4 text-left">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {currentJobs.map((job) => (
                        <tr key={job.id} className="group hover:bg-indigo-50/30 transition-colors duration-200">
                          <td className="px-6 py-4">
                            <div className="font-semibold text-slate-800 group-hover:text-indigo-700 transition-colors duration-200" style={{ fontFamily: theme.fonts.body }}>
                              {job.title}
                            </div>
                            <div className="text-sm text-indigo-600 mt-1">
                              {job.company_name}
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            {job.isExternal ? (
                              <span className="inline-flex items-center px-2.5 py-1 rounded-md bg-purple-50 text-purple-700 border border-purple-100 text-xs font-medium">
                                External Platform
                              </span>
                            ) : (
                              <span className="text-slate-600 text-sm">
                                {job.department_name || '-'}
                              </span>
                            )}
                          </td>
                          <td className="px-6 py-4 hidden md:table-cell">
                            <span className="inline-flex items-center px-2.5 py-1 rounded-md bg-blue-50/80 text-blue-700 border border-blue-200/60 text-xs font-medium">
                              <FiMapPin className="mr-1.5 w-3.5 h-3.5 text-blue-500" />
                              {job.location || 'Remote'}
                            </span>
                          </td>
                          <td className="px-6 py-4 hidden md:table-cell">
                            <div className="flex items-center text-slate-500 text-sm">
                              <FiCalendar className="mr-1.5 w-4 h-4" />
                              {formatDate(job.created_at)}
                            </div>
                          </td>
                          <td className="px-6 py-4 text-left">
                            <div className="flex justify-start items-center gap-2">
                              {job.isExternal ? (
                                <button
                                  onClick={() => window.open(job.url, '_blank')}
                                  title="Apply on Original Site"
                                  className="p-2 bg-indigo-50 hover:bg-indigo-100 text-indigo-600 rounded-lg transition-colors duration-200"
                                >
                                  <FiEye className="w-5 h-5" />
                                </button>
                              ) : (
                                <button
                                  onClick={() => handleViewJobDescription(job)}
                                  title="View Details"
                                  className="p-2 bg-slate-50 hover:bg-slate-100 text-slate-600 rounded-lg transition-colors duration-200"
                                >
                                  <FiEye className="w-5 h-5" />
                                </button>
                              )}
                              <button
                                onClick={() => handlePracticeInterview(job)}
                                disabled={applyingJobId === job.id}
                                className="ml-2 flex items-center justify-center px-4 py-2 bg-gradient-to-r from-indigo-500 to-purple-500 text-white rounded-xl text-sm font-medium transition-all duration-300 transform hover:scale-[1.02] hover:shadow-lg shadow-indigo-200/50 disabled:opacity-50"
                              >
                                {applyingJobId === job.id ? <CircularProgress size={16} color="inherit" className="mr-2" /> : <FiBriefcase className="w-4 h-4 mr-2" />}
                                {applyingJobId === job.id ? 'Starting...' : 'Practice Interview'}
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination Controls */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-between px-6 py-4 bg-slate-50 border-t border-slate-200">
                    <div className="text-sm text-slate-500">
                      Showing {((currentPage - 1) * rowsPerPage) + 1} to {Math.min(currentPage * rowsPerPage, filteredJobs.length)} of {filteredJobs.length} jobs
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                        className="px-3 py-1.5 border border-slate-200 rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        Previous
                      </button>
                      <button
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        className="px-3 py-1.5 border border-slate-200 rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        Next
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Job Description Modal */}
          <Modal
            isOpen={jobDescriptionDialogOpen}
            onClose={() => setJobDescriptionDialogOpen(false)}
            maxWidth="max-w-4xl"
          >
            <div className="px-8 py-6 border-b border-slate-200/50 bg-gradient-to-r from-slate-50 to-indigo-50/30">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h3 className="text-3xl text-slate-800 mb-3 leading-tight">
                    {selectedJob?.title}
                  </h3>
                  <div className="flex flex-wrap items-center gap-6">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-indigo-500 rounded-full"></div>
                      <span className="text-lg text-indigo-600">
                        {selectedJob?.company_name}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <FiBriefcase className="w-4 h-4 text-slate-500" />
                      <span className="text-sm font-normal text-slate-600">
                        {selectedJob?.department_name}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <FiMapPin className="w-4 h-4 text-blue-500" />
                      <span className="text-sm font-medium text-blue-600">
                        {selectedJob?.location || 'Remote'}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <FiDollarSign className="w-4 h-4 text-emerald-500" />
                      <span className="text-sm font-medium text-emerald-600">
                        {selectedJob?.required_credits} {t('publicJobDescriptions.credits')}
                      </span>
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => setJobDescriptionDialogOpen(false)}
                  className="ml-6 p-3 hover:bg-slate-100 rounded-full transition-all duration-200 group"
                >
                  <FiX className="w-5 h-5 text-slate-500 group-hover:text-slate-700" />
                </button>
              </div>
            </div>

            <div className="px-8 py-8">
              <h4 className="text-xl text-slate-800 mb-6">
                {t('publicJobDescriptions.jobDescription')}
              </h4>
              <div
                className="mb-6 text-slate-700 leading-relaxed space-y-4"
                dangerouslySetInnerHTML={{ __html: selectedJob?.description }}
              />
              <div className="flex items-center gap-2 text-sm font-normal text-slate-500 bg-slate-50 px-4 py-2 rounded-xl">
                <FiCalendar className="w-4 h-4" />
                <span className="">
                  {t('publicJobDescriptions.postedOn')} {selectedJob && formatDate(selectedJob.created_at)}
                </span>
              </div>
            </div>

            <div className="px-8 py-6 bg-gradient-to-r from-slate-50 to-indigo-50/30 flex items-center justify-end gap-4">
              <button
                onClick={() => setJobDescriptionDialogOpen(false)}
                className="px-6 py-3 text-slate-600 hover:text-slate-800 transition-colors rounded-xl hover:bg-white/70"
              >
                {t('publicJobDescriptions.close')}
              </button>
              <button
                onClick={() => handlePreviewMatch(selectedJob)}
                disabled={previewLoading}
                className="flex items-center px-8 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white rounded-xl transition-all duration-300 disabled:opacity-50 shadow-lg hover:shadow-xl transform hover:scale-105"
              >
                {previewLoading ? (
                  <div className="w-4 h-4 mr-2 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  <FiEye className="w-4 h-4 mr-2" />
                )}
                {t('publicJobDescriptions.previewMatch')}
              </button>
              <button className="px-8 py-3 bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white rounded-xl transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105">
                {t('publicJobDescriptions.applyNow')}
              </button>
            </div>
          </Modal>

          {/* Enhanced Preview Match Modal */}
          <Modal
            isOpen={previewDialogOpen}
            onClose={() => setPreviewDialogOpen(false)}
            maxWidth="max-w-3xl"
          >
            <div className="px-8 py-6 border-b border-slate-200/50 bg-gradient-to-r from-slate-50 to-purple-50/30">
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="text-2xl text-slate-800 mb-2">
                    {t('publicJobDescriptions.skillMatchAnalysis')}
                  </h3>
                  {selectedJob && (
                    <p className="text-slate-600">
                      {selectedJob.title} {t('common.at')} {selectedJob.company_name}
                    </p>
                  )}
                </div>
                <button
                  onClick={() => setPreviewDialogOpen(false)}
                  className="p-3 hover:bg-slate-100 rounded-full transition-all duration-200 group"
                >
                  <FiX className="w-5 h-5 text-slate-500 group-hover:text-slate-700" />
                </button>
              </div>
            </div>

            <div className="px-8 py-8">
              {matchData ? (
                <div>
                  <p className="text-slate-800 mb-6">
                    {t('publicJobDescriptions.skillsMatchDescription')}
                  </p>
                  <div className="bg-slate-50 rounded-2xl p-6 border border-slate-200">
                    <pre className="text-sm font-normal text-slate-700 overflow-auto whitespace-pre-wrap">
                      {JSON.stringify(matchData, null, 2)}
                    </pre>
                  </div>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-4 border-indigo-200 border-t-indigo-600 mb-4"></div>
                  <p className="text-slate-600">{t('publicJobDescriptions.analyzingSkillMatch')}</p>
                </div>
              )}
            </div>

            <div className="px-8 py-6 bg-gradient-to-r from-slate-50 to-purple-50/30 flex items-center justify-end gap-4">
              <button
                onClick={() => setPreviewDialogOpen(false)}
                className="px-6 py-3 text-slate-600 hover:text-slate-800 transition-colors rounded-xl hover:bg-white/70"
              >
                {t('publicJobDescriptions.close')}
              </button>
              {matchData && (
                <button className="px-8 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white rounded-xl transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105">
                  {t('publicJobDescriptions.applyNow')}
                </button>
              )}
            </div>
          </Modal>
          </div>
        </PageCard>
      </PageShell>
      <B2CInterviewSetupModal
        isOpen={showPracticeModal}
        onClose={() => setShowPracticeModal(false)}
        resume={selectedResumeData}
        onStartInterview={(sessionId, inviteToken) => {
          let baseInterviewUrl = process.env.REACT_APP_INTERVIEW_UI_URL || window.location.origin;
          const relativePath = inviteToken ? `/interview/invite/${inviteToken}` : `/interview/${sessionId}?startInterview=true`;
          window.open(`${baseInterviewUrl}${relativePath}`, '_blank');
        }}
        creditBalance={credits}
        initialSkills={selectedPracticeJob?.required_skills || []}
        initialExperienceLevel={selectedPracticeJob?.experience_level}
        jobDescription={selectedPracticeJob?.description ? stripHtml(selectedPracticeJob.description) : ''}
        targetRole={selectedPracticeJob?.title}
      />
    </>
  );
}

export default PublicJobs;
